import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  SOURCE_ADMIN_SMS,
  SOURCE_TCAPI,
} from '@rsos/constants/locationSources';
import type { conversationStateValues } from './conversations.typings';
import { setConversationUnread } from './conversationsSlice';
import { getCallFromConversationID } from './utils';

interface State {
  conversations: Record<string, conversationStateValues>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TalkJSSession = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TalkJSMessage = any;

const useHandleUnreadConversations = (session: TalkJSSession) => {
  const dispatch = useDispatch();
  const conversations = useSelector((state: State) => state.conversations);
  const conversationListenerSetup = useRef(false);
  const conversationsRef = useRef(conversations);

  // Update a reference to conversations so the session useEffect is up to date
  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  // onMessage() functions stack not replace. Thus, need to ensure only gets called ONCE
  useEffect(() => {
    if (!session?.onMessage || conversationListenerSetup.current) {
      return;
    }

    conversationListenerSetup.current = true;

    session.onMessage((message: TalkJSMessage) => {
      const conversationID: string = message.conversation.id;
      const conversationPrefix = conversationID.split('.')[0];
      const acceptablePrefixes = new Set([SOURCE_TCAPI, SOURCE_ADMIN_SMS]);

      // only process tcapi conversations. Probably can make it more generic
      // by looking for tf911, rcs, and regular conversation id similarities
      if (!acceptablePrefixes.has(conversationPrefix)) {
        return;
      }

      const matchingCall = getCallFromConversationID(
        conversationID,
        conversationsRef.current,
      );

      if (matchingCall) {
        dispatch(
          setConversationUnread({
            callerID: matchingCall,
            isUnread: true,
          }),
        );
      }
    });

    return () => {
      conversationListenerSetup.current = false;
    };
  }, [dispatch, session]);
};

export default useHandleUnreadConversations;
